import React from "react";
import { Check, X, Sparkles, Copy, CheckCircle2, AlertTriangle } from "lucide-react";
import {
  PASSWORD_RULES,
  isPasswordValid,
  passwordStrength,
  generateStrongPassword,
  checkPwnedPassword,
} from "../lib/passwordPolicy";

/**
 * Iter269.10/.11 — Inline live policy meter + strength bar + suggester.
 *
 * Props:
 *   value             — current password string (parent owns state)
 *   onValidityChange  — fired with (bool) when overall validity flips
 *   onBreachChange    — optional. Fired with `{pwned, count}|null` when
 *                       the HIBP k-anonymity check resolves. Parents
 *                       (PasswordWithMeter) use it to optionally gate
 *                       submit on a breach. The meter itself never
 *                       gates anything — visual signal only.
 *   onSuggest         — optional. If supplied, a "Suggest a strong
 *                       password" button is rendered. Clicking it calls
 *                       `onSuggest(suggestedString)` so the parent can
 *                       fill BOTH password + confirm inputs in one go.
 *   showStrengthBar   — default true. 0-100 visual reinforcement only —
 *                       does NOT gate submit (the rules do that).
 *   showCopy          — default true when onSuggest fires; renders a
 *                       small Copy button next to the suggested value.
 *   breachCheckEnabled — default true. Set false in test/demo envs.
 *   testIdPrefix      — root + per-rule test-id stem.
 */
export default function PasswordPolicyMeter({
  value = "",
  onValidityChange,
  onBreachChange,
  onSuggest,
  showStrengthBar = true,
  showCopy = true,
  breachCheckEnabled = true,
  testIdPrefix = "pw-policy",
  className = "",
}) {
  const isEmpty = !value;
  const overall = isPasswordValid(value);
  const strength = passwordStrength(value);

  // Emit validity up-stream whenever it flips.
  const lastEmittedRef = React.useRef(null);
  React.useEffect(() => {
    if (overall !== lastEmittedRef.current && onValidityChange) {
      lastEmittedRef.current = overall;
      onValidityChange(overall);
    }
  }, [overall, onValidityChange]);

  // Iter269.13 — HIBP k-anonymity breach check.
  // Status: 'idle' (empty / sub-rules) · 'checking' · 'ok' · 'pwned' · 'error'
  const [breach, setBreach] = React.useState({ status: "idle", count: 0 });
  const lastBreachEmittedRef = React.useRef("idle");

  React.useEffect(() => {
    if (!breachCheckEnabled) {
      setBreach({ status: "idle", count: 0 });
      return undefined;
    }
    // Don't run the check until the basic rules pass — saves the
    // round-trip while the user is still composing.
    if (!overall) {
      setBreach({ status: "idle", count: 0 });
      return undefined;
    }
    const ctl = new AbortController();
    setBreach((b) => ({ ...b, status: "checking" }));
    // 600ms debounce so we don't fire on every keystroke at the end of
    // a long password.
    const t = setTimeout(async () => {
      const res = await checkPwnedPassword(value, ctl.signal);
      if (ctl.signal.aborted) return;
      if (!res) {
        setBreach({ status: "error", count: 0 });
      } else if (res.pwned) {
        setBreach({ status: "pwned", count: res.count });
      } else {
        setBreach({ status: "ok", count: 0 });
      }
    }, 600);
    return () => { clearTimeout(t); ctl.abort(); };
  }, [value, overall, breachCheckEnabled]);

  // Notify upstream when the breach result flips. We pass the full
  // status so PasswordWithMeter can fail-open on errors.
  React.useEffect(() => {
    if (breach.status === lastBreachEmittedRef.current) return;
    lastBreachEmittedRef.current = breach.status;
    if (onBreachChange) {
      onBreachChange(breach.status === "pwned"
        ? { pwned: true, count: breach.count, status: "pwned" }
        : { pwned: false, count: 0, status: breach.status });
    }
  }, [breach, onBreachChange]);

  // Iter269.11 — "Just suggested" state for the suggest button. After
  // generating, we briefly show the generated password as plaintext
  // inline so the user can verify what got typed in. State auto-clears
  // after 12 seconds for safety.
  const [lastSuggested, setLastSuggested] = React.useState("");
  const [copied, setCopied] = React.useState(false);
  React.useEffect(() => {
    if (!lastSuggested) return undefined;
    const t = setTimeout(() => { setLastSuggested(""); setCopied(false); }, 12000);
    return () => clearTimeout(t);
  }, [lastSuggested]);

  const handleSuggest = () => {
    const pw = generateStrongPassword(16);
    setLastSuggested(pw);
    setCopied(false);
    onSuggest?.(pw);
  };

  const handleCopy = async () => {
    if (!lastSuggested) return;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(lastSuggested);
      } else {
        // Fallback for older browsers — no fancy textarea, just give up
        // silently. Modern browsers all support clipboard API.
      }
      setCopied(true);
    } catch { /* swallow */ }
  };

  // Strength bar colour follows a smooth 6-stop gradient as the score
  // climbs from 0 → 100. The fill is a SOLID colour at any moment
  // (not a striped rainbow) — the colour itself is what changes as
  // the user types. Stops were picked to read naturally as
  // red → red-orange → orange → orange-yellow → yellow → green
  // without any flat plateau in the middle.
  //
  // Iter269.13 — if HIBP says this password is in a breach corpus, we
  // override the bar to RED regardless of score. The rules still pass —
  // only the visual signal changes.
  const _STRENGTH_STOPS = [
    { at:   0, rgb: [255, 110, 110] },  // pure red
    { at:  20, rgb: [255, 140,  85] },  // red / orange
    { at:  40, rgb: [255, 179,  71] },  // orange
    { at:  60, rgb: [255, 210,  77] },  // orange / yellow
    { at:  80, rgb: [232, 233,  75] },  // yellow
    { at: 100, rgb: [ 93, 214, 143] },  // green
  ];
  const _gradientColour = (score) => {
    const s = Math.max(0, Math.min(100, score));
    let lo = _STRENGTH_STOPS[0];
    let hi = _STRENGTH_STOPS[_STRENGTH_STOPS.length - 1];
    for (let i = 0; i < _STRENGTH_STOPS.length - 1; i++) {
      if (s >= _STRENGTH_STOPS[i].at && s <= _STRENGTH_STOPS[i + 1].at) {
        lo = _STRENGTH_STOPS[i];
        hi = _STRENGTH_STOPS[i + 1];
        break;
      }
    }
    const span = hi.at - lo.at;
    const t = span === 0 ? 0 : (s - lo.at) / span;
    const mix = (a, b) => Math.round(a + (b - a) * t);
    return `rgb(${mix(lo.rgb[0], hi.rgb[0])}, ${mix(lo.rgb[1], hi.rgb[1])}, ${mix(lo.rgb[2], hi.rgb[2])})`;
  };

  const effectiveScore = breach.status === "pwned"
    ? Math.min(strength.score, 18)
    : strength.score;
  // Compute label tier from the EFFECTIVE score so "weak" reads
  // correctly when the bar's been overridden by a breach hit.
  let effectiveTier;
  if (breach.status === "pwned")  effectiveTier = "breached";
  else if (effectiveScore >= 80)  effectiveTier = "strong";
  else if (effectiveScore >= 60)  effectiveTier = "medium";
  else if (effectiveScore >= 40)  effectiveTier = "okay";
  else if (effectiveScore >= 20)  effectiveTier = "fair";
  else if (effectiveScore > 0)    effectiveTier = "weak";
  else                            effectiveTier = "empty";

  const barColour = isEmpty
    ? "var(--qt-text-3)"
    : breach.status === "pwned"
      ? "rgb(255, 110, 110)"
      : _gradientColour(effectiveScore);
  const strengthLabel = breach.status === "pwned"
    ? "exposed in breach"
    : effectiveTier;

  return (
    <div
      className={`mt-2 ${className}`}
      data-testid={testIdPrefix}
      role="group"
      aria-label="Password policy"
    >
      {showStrengthBar && (
        <div
          className="mb-2"
          data-testid={`${testIdPrefix}-strength`}
          data-tier={effectiveTier}
          data-score={effectiveScore}
          data-breach={breach.status}
        >
          <div
            style={{
              height: 4,
              width: "100%",
              background: "rgba(255,255,255,0.06)",
              borderRadius: 2,
              overflow: "hidden",
            }}
          >
            <div
              style={{
                height: "100%",
                width: `${effectiveScore}%`,
                background: barColour,
                transition: "width 160ms ease, background-color 160ms ease",
              }}
            />
          </div>
          <div
            className="qt-mono text-[10px] mt-1 flex justify-between"
            style={{
              color: barColour,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
            }}
          >
            <span>
              {isEmpty
                ? "—"
                : `Strength · ${strengthLabel}${breach.status === "checking" ? " · checking…" : ""}`}
            </span>
            {!isEmpty && breach.status !== "pwned" && <span>{effectiveScore}/100</span>}
          </div>
        </div>
      )}

      <div
        className="qt-mono text-[11px]"
        role="list"
        style={{ lineHeight: 1.7 }}
      >
        {PASSWORD_RULES.map((rule) => {
          const passed = rule.ok(value);
          const colour = isEmpty
            ? "var(--qt-text-3)"
            : passed
              ? "var(--qt-cyan)"
              : "#FF6E6E";
          const Icon = passed ? Check : X;
          return (
            <div
              key={rule.id}
              role="listitem"
              data-testid={`${testIdPrefix}-${rule.id}`}
              data-passed={passed ? "yes" : "no"}
              className="flex items-center gap-2"
              style={{ color: colour }}
            >
              <Icon size={12} strokeWidth={3} />
              <span>{rule.label}</span>
            </div>
          );
        })}
      </div>

      {breach.status === "pwned" && (
        <div
          className="qt-mono text-[11px] mt-3 px-3 py-2 rounded flex items-start gap-2"
          data-testid={`${testIdPrefix}-breach`}
          data-breach-count={breach.count}
          style={{
            background: "rgba(255, 110, 110, 0.08)",
            border: "1px solid rgba(255, 110, 110, 0.4)",
            color: "#FF6E6E",
            lineHeight: 1.55,
          }}
        >
          <AlertTriangle size={14} className="mt-[1px] flex-shrink-0" />
          <span>
            <strong style={{ letterSpacing: "0.05em" }}>EXPOSED IN A BREACH.</strong>
            {" "}This password has appeared in {breach.count.toLocaleString()} known
            data breaches. Pick a different one — use the suggester below for a
            never-leaked alternative.
          </span>
        </div>
      )}

      {onSuggest && (
        <div className="mt-3" data-testid={`${testIdPrefix}-suggest-row`}>
          <button
            type="button"
            onClick={handleSuggest}
            data-testid={`${testIdPrefix}-suggest-btn`}
            className="qt-mono text-[11px] inline-flex items-center gap-1.5"
            style={{
              color: "var(--qt-cyan)",
              background: "transparent",
              border: "1px solid rgba(0, 229, 255, 0.35)",
              borderRadius: 3,
              padding: "4px 10px",
              cursor: "pointer",
              letterSpacing: "0.08em",
            }}
          >
            <Sparkles size={12} />
            SUGGEST A STRONG PASSWORD
          </button>
          {lastSuggested && (
            <div
              className="qt-mono text-[11px] mt-2 inline-flex items-center gap-2 ml-2"
              data-testid={`${testIdPrefix}-suggest-preview`}
              style={{
                color: "var(--qt-text-2)",
                padding: "4px 8px",
                background: "rgba(0,229,255,0.06)",
                borderRadius: 3,
              }}
            >
              <span style={{ letterSpacing: "0.04em" }}>{lastSuggested}</span>
              {showCopy && (
                <button
                  type="button"
                  onClick={handleCopy}
                  data-testid={`${testIdPrefix}-suggest-copy`}
                  className="inline-flex items-center"
                  style={{
                    color: copied ? "var(--qt-cyan)" : "var(--qt-text-3)",
                    background: "transparent",
                    border: "none",
                    padding: 0,
                    cursor: "pointer",
                  }}
                  aria-label={copied ? "Copied" : "Copy suggestion"}
                >
                  {copied ? <CheckCircle2 size={12} /> : <Copy size={12} />}
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
